import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const integrations = [
  {
    category: 'Communication',
    items: [
      { name: 'Slack', description: 'Send messages and notifications to Slack channels', icon: '💬' },
      { name: 'Microsoft Teams', description: 'Integrate with Teams for seamless collaboration', icon: '👥' },
      { name: 'Discord', description: 'Connect your Discord server for community management', icon: '🎮' },
      { name: 'Telegram', description: 'Bot integration for Telegram messaging', icon: '✈️' },
    ],
  },
  {
    category: 'CRM & Sales',
    items: [
      { name: 'Salesforce', description: 'Sync leads and contacts with Salesforce', icon: '☁️' },
      { name: 'HubSpot', description: 'Manage your HubSpot CRM from SweetPilot', icon: '🟠' },
      { name: 'Pipedrive', description: 'Track deals and pipeline in Pipedrive', icon: '📊' },
      { name: 'Zoho CRM', description: 'Connect your Zoho CRM account', icon: '📋' },
    ],
  },
  {
    category: 'Marketing',
    items: [
      { name: 'Mailchimp', description: 'Create and manage email campaigns', icon: '📧' },
      { name: 'Buffer', description: 'Schedule social media posts', icon: '📱' },
      { name: 'Hootsuite', description: 'Manage all your social accounts', icon: '🦉' },
      { name: 'Google Analytics', description: 'Track and analyze website traffic', icon: '📈' },
    ],
  },
  {
    category: 'E-commerce',
    items: [
      { name: 'Shopify', description: 'Manage your Shopify store', icon: '🛍️' },
      { name: 'WooCommerce', description: 'Integrate with WordPress shops', icon: '🛒' },
      { name: 'Stripe', description: 'Process payments with Stripe', icon: '💳' },
      { name: 'PayPal', description: 'Accept PayPal payments', icon: '🅿️' },
    ],
  },
  {
    category: 'Productivity',
    items: [
      { name: 'Google Workspace', description: 'Connect Gmail, Drive, Calendar, and more', icon: '🔵' },
      { name: 'Microsoft 365', description: 'Integrate with Outlook, OneDrive, and Office', icon: '🟦' },
      { name: 'Notion', description: 'Sync with your Notion workspace', icon: '📝' },
      { name: 'Asana', description: 'Manage tasks and projects in Asana', icon: '🎯' },
    ],
  },
  {
    category: 'Automation',
    items: [
      { name: 'Zapier', description: 'Connect with 5000+ apps via Zapier', icon: '⚡' },
      { name: 'Make', description: 'Build powerful automations with Make', icon: '🔧' },
      { name: 'n8n', description: 'Self-hosted workflow automation', icon: '🔄' },
      { name: 'Webhooks', description: 'Custom webhook integrations', icon: '🪝' },
    ],
  },
];

export default function IntegrationsPage() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Integrations"
      subtitle="Connect SweetPilot with your favorite tools and automate your workflows."
    >
      {/* Integration Categories */}
      {integrations.map((category, categoryIndex) => (
        <section key={category.category} className="mb-12">
          <h2 className={`text-xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.category}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {category.items.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (categoryIndex * 4 + index) * 0.02 }}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-all group ${
                  isDark
                    ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                    : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm hover:shadow-md'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform ${
                  isDark ? 'bg-dark-700' : 'bg-slate-100'
                }`}>
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.name}</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{item.description}</p>
                </div>
                <Check className={`w-5 h-5 shrink-0 mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </motion.div>
            ))}
          </div>
        </section>
      ))}

      {/* API Section */}
      <section className={`mb-16 p-8 rounded-2xl border ${
        isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h2 className={`text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Build Custom Integrations</h2>
            <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Use our powerful REST API to build custom integrations and automate any workflow.
              Full documentation, SDKs, and examples available.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/api"
                className={`flex items-center gap-2 font-medium ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                View API Docs <ArrowRight className="w-4 h-4" />
              </Link>
              <span className={isDark ? 'text-gray-600' : 'text-slate-300'}>|</span>
              <a
                href="https://github.com/flowai"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 font-medium ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              >
                GitHub SDKs <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div className={`p-4 rounded-xl font-mono text-sm overflow-x-auto ${
            isDark ? 'bg-dark-900 text-gray-400' : 'bg-slate-100 text-slate-600'
          }`}>
            <pre>{`curl -X POST https://api.flowai.com/v1/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{"employee": "aria", "message": "..."}'`}</pre>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Need a Custom Integration?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Our team can help you build custom integrations for your specific needs.
        </p>
        <a
          href="mailto:integrations@sweetpilot.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Contact Us
        </a>
      </section>
    </StaticPageLayout>
  );
}
